/*
 * Author: Harper Yan
 * File Created: Monday, 25-04-07 05:00 pm
 * Last Modified: Monday, 25-05-19 03:30 pm, by Chao.Wang
 */

import { Logger } from '@web/utils';

import type { ErrorCodes, ErrorDetail } from './@type';
import { queryInfraErrorCodesByScope } from './services';

type ErrorMap = Record<string, ErrorCodes | undefined>;

export type DEFAULT_LOCALE = 'en' | 'zh';

const DEFAULT_ERROR_MAP: Record<DEFAULT_LOCALE, ErrorDetail> = {
  en: {
    msg: 'The system is busy, please try again later',
    btn1: 'Back',
    action: 'modal',
  },
  zh: {
    msg: '系统繁忙，请稍后再试',
    btn1: '返回',
    action: 'modal',
  },
};

type TemplateData = Record<string, string | number> | null | undefined | string;

export function renderTemplate(template: string, data: TemplateData): string {
  if (data === null || data === undefined || typeof data !== 'object') {
    return template; // 如果 data 是 null, undefined 或字符串，返回原模板
  }

  // eslint-disable-next-line prefer-named-capture-group -- ignore
  return template.replace(/{{\s*([^{}\s]+)\s*}}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(data, key)
      ? String(data[key])
      : match;
  });
}

export type ErrorCodeManagerProps = {
  showErrorInfo: (detail: ErrorDetail, data: TemplateData) => void;
  scope?: string | string[];
  defaultErrorMap?: Record<DEFAULT_LOCALE, ErrorDetail>;
};

export class ErrorCodeManager {
  private static _instance: ErrorCodeManager | null = null;
  private _errorMaps: ErrorMap = {
    en: undefined,
    zh: undefined,
  };
  private _scope: string | string[];
  private _defaultErrorMap: Record<DEFAULT_LOCALE, ErrorDetail>;

  private _showErrorInfo: (detail: ErrorDetail, data: TemplateData) => void;

  private _logger: Logger;

  private constructor({
    showErrorInfo,
    scope,
    defaultErrorMap,
  }: ErrorCodeManagerProps) {
    this._showErrorInfo = showErrorInfo;
    this._scope = scope || '';
    this._defaultErrorMap = defaultErrorMap || DEFAULT_ERROR_MAP;
    this._logger = new Logger({ module: 'error-code-manager' });
  }

  public static initialize(params: ErrorCodeManagerProps): ErrorCodeManager {
    if (!ErrorCodeManager._instance) {
      ErrorCodeManager._instance = new ErrorCodeManager(params);
    }
    return ErrorCodeManager._instance;
  }

  async getErrorCodes(locale: DEFAULT_LOCALE): Promise<ErrorCodes | undefined> {
    // Return cached error codes if they exist
    if (this._errorMaps[locale]) {
      return this._errorMaps[locale] || this._errorMaps['en'];
    }

    // Otherwise fetch from API
    try {
      const newErrorCodes = await this._fetchInfraErrorCode(locale);

      this._errorMaps[locale] = newErrorCodes;

      return this._errorMaps[locale];
    } catch (error) {
      this._logger.error('Error fetching error codes:', error);

      return undefined;
    }
  }

  private async _fetchInfraErrorCode(locale: DEFAULT_LOCALE = 'en') {
    const scope = typeof this._scope === 'string' ? [this._scope] : this._scope;
    return await queryInfraErrorCodesByScope(locale, scope);
  }

  async getDetail(
    errorCode?: string,
    locale: DEFAULT_LOCALE = 'en'
  ): Promise<ErrorDetail> {
    if (!errorCode) {
      return this._defaultErrorMap[locale] || this._errorMaps['en'];
    }

    const errorCodes = await this.getErrorCodes(locale);

    if (!errorCode || !errorCodes) {
      return this._defaultErrorMap[locale];
    }

    return errorCodes[errorCode] || this._defaultErrorMap[locale];
  }

  showErrorInfo(detail: ErrorDetail, data: TemplateData) {
    if (typeof window === 'undefined') {
      return;
    }

    this._showErrorInfo(detail, data);
  }
}
